import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import { Sparkles, Lock } from "lucide-react";
import type { MeResponse } from "../../lib/auth";
import { PLAN_RANK } from "@brandecho/shared";

interface OutletCtx {
  me: MeResponse;
  refreshMe: () => Promise<void>;
}

type Platform = "twitter" | "bluesky" | "linkedin" | "threads";

const PLATFORM_LABEL: Record<Platform, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  threads: "Threads",
};

const PLATFORM_ORDER: Platform[] = ["twitter", "bluesky", "linkedin", "threads"];

interface AutoPlug {
  id: string;
  name: string;
  link_url: string | null;
  intent: string;
  delay_hours: number;
  voice_profile_id: string | null;
  voice_profile_name: string | null;
  platforms: Platform[];
  status: "active" | "paused";
  ai_model: string | null;
  scheduled_count: number;
  failed_count: number;
  last_run_at: number | null;
  created_at: number;
  updated_at: number;
}

interface VoiceProfileLite {
  id: string;
  name: string;
  status: string;
}

interface RunRow {
  id: string;
  auto_plug_id: string;
  config_name: string;
  origin_external_post_url: string | null;
  origin_content: string | null;
  origin_platform: Platform | null;
  generated_text: string | null;
  reply_status: string | null;
  reply_external_url: string | null;
  reply_scheduled_for: number | null;
  status: "pending" | "generating" | "scheduled" | "completed" | "failed";
  error_message: string | null;
  created_at: number;
}

const STATUS_BADGE: Record<RunRow["status"], string> = {
  pending: "bg-ink-100 text-ink-700",
  generating: "bg-amber-100 text-amber-800",
  scheduled: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

function fmtDate(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function AutoPlugPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const ctx = useOutletContext<OutletCtx | null>();
  const plan = ctx?.me?.credits?.plan ?? "free";
  const proGated = PLAN_RANK[plan] < PLAN_RANK.pro;
  const [configs, setConfigs] = useState<AutoPlug[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [voices, setVoices] = useState<VoiceProfileLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AutoPlug | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [intent, setIntent] = useState("");
  const [delayHours, setDelayHours] = useState(6);
  const [voiceId, setVoiceId] = useState("");
  const [platforms, setPlatforms] = useState<Set<Platform>>(new Set(["twitter"]));

  const activeConfigs = useMemo(
    () => configs.filter((c) => c.status === "active"),
    [configs]
  );
  const pausedConfigs = useMemo(
    () => configs.filter((c) => c.status === "paused"),
    [configs]
  );

  async function load() {
    setLoading(true);
    try {
      const [cfg, rn, vp] = await Promise.all([
        api.get<{ auto_plugs: AutoPlug[] }>("/api/auto-plugs"),
        api.get<{ runs: RunRow[] }>("/api/auto-plugs/runs?limit=30"),
        api.get<{ voice_profiles: VoiceProfileLite[] }>("/api/voice-profiles"),
      ]);
      setConfigs(cfg.auto_plugs ?? []);
      setRuns(rn.runs ?? []);
      setVoices((vp.voice_profiles ?? []).filter((v) => v.status === "ready"));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetForm() {
    setName("");
    setLinkUrl("");
    setIntent("");
    setDelayHours(6);
    setVoiceId("");
    setPlatforms(new Set(["twitter"]));
    setEditing(null);
    setShowForm(false);
  }

  function openCreate() {
    resetForm();
    setShowForm(true);
  }

  function openEdit(c: AutoPlug) {
    setName(c.name);
    setLinkUrl(c.link_url ?? "");
    setIntent(c.intent);
    setDelayHours(c.delay_hours);
    setVoiceId(c.voice_profile_id ?? "");
    setPlatforms(new Set(c.platforms));
    setEditing(c);
    setShowForm(true);
  }

  function togglePlatform(p: Platform) {
    setPlatforms((cur) => {
      const next = new Set(cur);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !intent.trim() || platforms.size === 0) return;
    if (intent.trim().length < 8) {
      toast.error("Intent must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        link_url: linkUrl.trim() || null,
        intent: intent.trim(),
        delay_hours: delayHours,
        voice_profile_id: voiceId || null,
        platforms: Array.from(platforms),
      };
      if (editing) {
        await api.patch(`/api/auto-plugs/${editing.id}`, body);
        toast.success("Auto-plug updated.");
      } else {
        await api.post("/api/auto-plugs", body);
        toast.success("Auto-plug created. It will run on your next published post.");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleStatus(c: AutoPlug) {
    try {
      await api.patch(`/api/auto-plugs/${c.id}`, {
        status: c.status === "active" ? "paused" : "active",
      });
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove(c: AutoPlug) {
    const ok = await confirm({
      title: "Delete auto-plug",
      message: `"${c.name}" will stop running. Past runs are kept for history.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/auto-plugs/${c.id}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  if (proGated) {
    return (
      <div>
        <header className="mb-6">
          <h1 className="page-title">Auto-plug</h1>
          <p className="page-subtitle">
            Every time you publish a post, BrandEcho writes a voice-matched
            follow-up and schedules it N hours later.
          </p>
        </header>
        <div className="rounded-2xl border border-brand-200/70 bg-gradient-to-br from-brand-50 via-white to-brand-50/40 p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-sm">
            <Lock className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-ink-900">
            Auto-plug is a Pro feature
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-ink-600">
            On Pro and Studio, every post you publish gets a voice-matched
            follow-up scheduled automatically. Plus you unlock flagship and
            reasoning AI models.
          </p>
          <Link
            to="/pricing"
            className="btn-brand mt-5 inline-flex no-underline"
          >
            <Sparkles className="h-4 w-4" />
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="page-title">Auto-plug</h1>
        <p className="page-subtitle">
          Every time you publish a post, BrandEcho writes a voice-matched follow-up
          and schedules it N hours later. Each follow-up is freshly generated from
          your brand voice + the original post — never a stale template.
        </p>
      </header>

      <div className="mb-6 flex items-center justify-between">
        <div className="text-sm text-ink-600">
          {loading ? "Loading…" : `${activeConfigs.length} active · ${pausedConfigs.length} paused`}
        </div>
        {!showForm ? (
          <button onClick={openCreate} className="btn-brand">
            + New auto-plug
          </button>
        ) : null}
      </div>

      {showForm ? (
        <form
          onSubmit={submit}
          className="mb-8 space-y-4 rounded-xl border border-ink-200 bg-white p-5 shadow-sm"
        >
          <h2 className="text-lg font-medium text-ink-900">
            {editing ? "Edit auto-plug" : "New auto-plug"}
          </h2>

          <div>
            <label className="mb-1 block text-sm text-ink-700">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Newsletter signup CTA"
              maxLength={80}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-ink-700">
              Intent <span className="text-ink-400">(what to promote)</span>
            </label>
            <textarea
              className="input min-h-[88px]"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="e.g., Mention my weekly Substack newsletter on building voice-aware AI tools and invite readers to subscribe."
              maxLength={500}
              required
            />
            <div className="mt-1 text-xs text-ink-500">
              {intent.length}/500 — describe what you want plugged. The AI rewrites this naturally for each post.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm text-ink-700">
              Link <span className="text-ink-400">(optional)</span>
            </label>
            <input
              className="input"
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://yourdomain.com/newsletter"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm text-ink-700">
                Delay after publish
              </label>
              <select
                className="input"
                value={delayHours}
                onChange={(e) => setDelayHours(parseInt(e.target.value, 10))}
              >
                <option value={1}>1 hour</option>
                <option value={3}>3 hours</option>
                <option value={6}>6 hours</option>
                <option value={12}>12 hours</option>
                <option value={24}>24 hours</option>
                <option value={48}>2 days</option>
                <option value={72}>3 days</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm text-ink-700">
                Voice <span className="text-ink-400">(optional)</span>
              </label>
              <select
                className="input"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
              >
                <option value="">— No voice (generic) —</option>
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm text-ink-700">Apply to platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_ORDER.map((p) => (
                <label
                  key={p}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition ${
                    platforms.has(p)
                      ? "border-brand-500 bg-brand-50 text-brand-900"
                      : "border-ink-200 bg-white text-ink-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={platforms.has(p)}
                    onChange={() => togglePlatform(p)}
                  />
                  {PLATFORM_LABEL[p]}
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn-primary text-sm" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save changes" : "Create auto-plug"}
            </button>
            <button type="button" onClick={resetForm} className="btn-secondary text-sm">
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <section className="mb-8">
        <h2 className="mb-3 text-base font-medium text-ink-900">Active</h2>
        {activeConfigs.length === 0 && !loading ? (
          <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
            No active auto-plugs yet. Create one above — it'll fire after your next published post.
          </div>
        ) : (
          <div className="space-y-3">
            {activeConfigs.map((c) => (
              <ConfigCard
                key={c.id}
                config={c}
                onEdit={() => openEdit(c)}
                onToggle={() => toggleStatus(c)}
                onDelete={() => remove(c)}
              />
            ))}
          </div>
        )}
      </section>

      {pausedConfigs.length > 0 ? (
        <section className="mb-8">
          <h2 className="mb-3 text-base font-medium text-ink-900">Paused</h2>
          <div className="space-y-3">
            {pausedConfigs.map((c) => (
              <ConfigCard
                key={c.id}
                config={c}
                onEdit={() => openEdit(c)}
                onToggle={() => toggleStatus(c)}
                onDelete={() => remove(c)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <h2 className="mb-3 text-base font-medium text-ink-900">Recent runs</h2>
        {runs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
            No runs yet. After your next published post, the matching auto-plug will appear here.
          </div>
        ) : (
          <div className="space-y-3">
            {runs.map((r) => (
              <RunCard key={r.id} run={r} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ConfigCard(props: {
  config: AutoPlug;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { config: c } = props;
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-ink-900">{c.name}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                c.status === "active"
                  ? "bg-green-100 text-green-800"
                  : "bg-ink-100 text-ink-700"
              }`}
            >
              {c.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-600">{truncate(c.intent, 200)}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-ink-500">
            <span>Delay {c.delay_hours}h</span>
            <span>·</span>
            <span>
              {c.platforms.map((p) => PLATFORM_LABEL[p]).join(" · ")}
            </span>
            {c.voice_profile_name ? (
              <>
                <span>·</span>
                <span>Voice: {c.voice_profile_name}</span>
              </>
            ) : null}
            {c.link_url ? (
              <>
                <span>·</span>
                <a
                  href={c.link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  link
                </a>
              </>
            ) : null}
          </div>
          <div className="mt-2 text-xs text-ink-400">
            {c.scheduled_count} scheduled · {c.failed_count} failed · last run{" "}
            {fmtDate(c.last_run_at)}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <button
            onClick={props.onEdit}
            className="rounded-md border border-ink-200 px-2 py-1 text-xs hover:bg-ink-50"
          >
            Edit
          </button>
          <button
            onClick={props.onToggle}
            className="rounded-md border border-ink-200 px-2 py-1 text-xs hover:bg-ink-50"
          >
            {c.status === "active" ? "Pause" : "Activate"}
          </button>
          <button
            onClick={props.onDelete}
            className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function RunCard({ run }: { run: RunRow }) {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-ink-500">
          <span className="font-medium text-ink-700">{run.config_name}</span>
          {run.origin_platform ? (
            <>
              <span>·</span>
              <span>{PLATFORM_LABEL[run.origin_platform]}</span>
            </>
          ) : null}
          <span>·</span>
          <span>{fmtDate(run.created_at)}</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[run.status]}`}>
          {run.status}
        </span>
      </div>

      {run.origin_content ? (
        <div className="mb-3 rounded-lg bg-ink-50 p-3 text-sm text-ink-700">
          <div className="mb-1 text-xs uppercase tracking-wide text-ink-400">
            Original post
          </div>
          <div className="whitespace-pre-wrap break-words">
            {truncate(run.origin_content, 300)}
          </div>
          {run.origin_external_post_url ? (
            <a
              href={run.origin_external_post_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-brand-600 hover:underline"
            >
              View original →
            </a>
          ) : null}
        </div>
      ) : null}

      {run.generated_text ? (
        <div className="rounded-lg border border-brand-100 bg-brand-50/40 p-3 text-sm text-ink-800">
          <div className="mb-1 text-xs uppercase tracking-wide text-brand-700">
            Auto-plug follow-up
            {run.reply_scheduled_for ? (
              <span className="ml-2 font-normal text-ink-500">
                scheduled {fmtDate(run.reply_scheduled_for)}
              </span>
            ) : null}
          </div>
          <div className="whitespace-pre-wrap break-words">{run.generated_text}</div>
          {run.reply_external_url ? (
            <a
              href={run.reply_external_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-brand-600 hover:underline"
            >
              View follow-up post →
            </a>
          ) : run.reply_status ? (
            <div className="mt-1 text-xs text-ink-500">Status: {run.reply_status}</div>
          ) : null}
        </div>
      ) : run.status === "failed" ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Failed: {run.error_message ?? "Unknown error"}
        </div>
      ) : run.status === "generating" ? (
        <div className="text-sm text-ink-500">Generating follow-up…</div>
      ) : null}

      <div className="mt-2">
        <Link to="/app/schedule" className="text-xs text-ink-400 hover:text-brand-600">
          See in schedule →
        </Link>
      </div>
    </div>
  );
}
